import { Box, Typography, useTheme } from '@mui/material'
import { GridColDef, GridColType } from '@mui/x-data-grid-premium'
import { StringTypeEnum } from '@shared/SchemaTypes'
import { ReactNode } from 'react'
import * as yup from 'yup'
import assert from 'assert'
import React from 'react'
import ViewNumber from '@react/components/elements/ViewNumber'
import ViewEthPoolString from '@react/components/elements/ViewEthPoolString'
import { AddressPill } from '@react/components/addresses/AddressPill'
import dayjs from 'dayjs'

export const getFieldSchemaDescription = (
  explorerSchema: yup.ObjectSchema<any>,
  property: string
): yup.SchemaDescription => {
  const propArray = property.split('.')
  const currentSchema = explorerSchema
  let fieldSchema = currentSchema.describe().fields[propArray[0]] as yup.SchemaDescription

  assert(fieldSchema, `Field schema is undefined ${property} `)
  if (propArray.length === 1) {
    return fieldSchema
  }

  for (let i = 1; i < propArray.length; i++) {
    const prop = propArray[i]
    fieldSchema = (fieldSchema as yup.SchemaObjectDescription).fields[prop] as yup.SchemaDescription
  }
  assert(fieldSchema, `Field schema is undefined ${property} `)
  return fieldSchema
}

function ExplorerHeader(props: { label: string }): React.ReactElement {
  const { label } = props
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
      <Typography color={theme.palette.info.main} align={'left'} variant={'captionBold'}>
        {label}
      </Typography>
    </Box>
  )
}

const valueFromRow = (row: any, property: string): any => {
  const propArray = property.split('.')
  let value = row
  for (let i = 0; i < propArray.length; i++) {
    const prop = propArray[i]
    assert(prop, `prop is undefined ${property} `)
    assert(value, `prop is undefined ${property} `)

    value = value[prop]

    if (value === undefined) {
      return undefined
    }
  }
  return value
}
export const columnFromSchema = (
  explorerSchema: yup.ObjectSchema<any>,
  property: string,
  etherscanUrl: string | undefined
): GridColDef => {
  const fieldSchemaDescription = getFieldSchemaDescription(explorerSchema, property)
  //  const propName: string = fieldSchemaDescription.meta?.propName ?? ''
  const label: string = fieldSchemaDescription.label ?? ' '
  const stringType = fieldSchemaDescription.meta?.stringType ?? ''
  const fieldType = fieldSchemaDescription.type
  let columnType = fieldType
  if (
    fieldType === 'string' &&
    (stringType === StringTypeEnum.STABLE_COINS || stringType === StringTypeEnum.PPT_SHARES)
  ) {
    columnType = 'erc20'
  }
  return {
    field: property,
    renderHeader: (_): ReactNode => {
      return <ExplorerHeader label={label} />
    },
    headerName: label,
    type: columnType as GridColType,
    width: 150,

    renderCell: (params) => {
      let value = valueFromRow(params.row, property)
      if (params.aggregation) {
        value = params.value
      }

      const poolTokenSymbol =
        params.row.symbol ?? params.row.poolOverviewCTO?.symbol ?? params.row.poolAccountCTO?.symbol ?? ''
      const liquidityTokenSymbol =
        params.row.liquidityTokenSymbol ??
        params.row.poolOverviewCTO?.liquidityTokenSymbol ??
        params.row.poolAccountCTO?.liquidityTokenSymbol ??
        ''

      if (stringType === StringTypeEnum.ETH_ADDR) {
        if (!value) return <Box sx={{ textAlign: 'center', marginRight: 6 }}>-</Box>
        return (
          <AddressPill
            addr={value}
            style={{ height: 20 }}
            labelStyle={{ fontSize: '12px', lineHeight: '1', zIndex: 5 }}
          />
        )
      }
      if (stringType === StringTypeEnum.ETH_POOL_ADDR) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            {value && <ViewEthPoolString value={value} label={label} inTable={true} />}
          </Box>
        )
      }
      if (stringType === StringTypeEnum.TX_HASH) {
        return (
          <div className="">
            {etherscanUrl && (
              <AddressPill
                addr={value}
                style={{ height: 20 }}
                labelStyle={{ fontSize: '12px', lineHeight: '12px', zIndex: 5 }}
                href={`${etherscanUrl}/${value}`}
              />
            )}
            {!etherscanUrl && <AddressPill addr={value} style={{ height: 20, fontSize: 12 }} />}

            {/* {value && <ViewEthPoolString value={value} label={label} inTable={true} />} */}
          </div>
        )
      }

      if (
        fieldType === 'number' ||
        stringType === StringTypeEnum.EXCHANGE_RATE ||
        stringType === StringTypeEnum.STABLE_COINS ||
        stringType === StringTypeEnum.PPT_SHARES ||
        stringType === StringTypeEnum.APR ||
        stringType === StringTypeEnum.SINGLE_DAY_APR
      ) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            {value !== undefined && (
              <ViewNumber
                value={value}
                label={label}
                inTable={true}
                fieldSchema={fieldSchemaDescription}
                dataValue={value}
                poolTokenSymbol={poolTokenSymbol}
                liquidityTokenSymbol={liquidityTokenSymbol}
              />
            )}
          </Box>
        )
      }
      const stateToString: undefined | Record<string, string> = fieldSchemaDescription.meta?.stateToString
      if (value && stateToString) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            <Typography align={'left'} variant={'caption'}>
              {stateToString[value]}
            </Typography>
          </Box>
        )
      }
      if (fieldType === 'array') {
        const arrayValue = value as any[]
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            {arrayValue.map((item: object, index: number) => {
              if (index < 2) {
                return (
                  <Typography key={index} align={'left'} variant={'caption'}>
                    {JSON.stringify(item)}
                  </Typography>
                )
              }
              return <> </>
            })}
            {arrayValue.length >= 2 && (
              <Typography align={'left'} variant={'caption'}>
                ...
              </Typography>
            )}
          </Box>
        )
      }
      return (
        <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
          <Typography align={'left'} variant={'caption'}>
            {value?.toString()}
          </Typography>
        </Box>
      )
    },
    valueFormatter: (value: any) => {
      if (value === undefined) {
        return ''
      }

      // const liquidityTokenSymbol = row?.liquidityTokenSymbol ?? row.poolOverviewCTO?.liquidityTokenSymbol ?? ''
      // const liquidityTokenSymbol = row?.liquidityTokenSymbol ?? toAssetCurrency(row.poolOverviewCTO?.liquidityAssetAddr)

      if (stringType === StringTypeEnum.STABLE_COINS) {
        return `${(value / 1e6).toFixed(2)}`
      }
      if (stringType === StringTypeEnum.PPT_SHARES) {
        return `${(value / 1e6).toFixed(2)}`
      }
      // if (stringType === StringTypeEnum.ETH_DATE_TIME) {
      //   return dayjs(value * 1000)
      //     .utc()
      //     .format('DD/MM/YYYY') // Format as DD/MM/YYYY
      // }
      // if (stringType === StringTypeEnum.ETH_TIME_OF_DAY) {
      //   return dayjs(value * 1000)
      //     .utc()
      //     .format('DD/MM/YYYY') // Format as DD/MM/YYYY
      // }
      // if (stringType === StringTypeEnum.ETH_DATE) {
      //   return dayjs(value * 1000)
      //     .utc()
      //     .format('DD/MM/YYYY') // Format as DD/MM/YYYY
      // }

      if (stringType === StringTypeEnum.ETH_DATE) {
        const d = new Date(Number(value) * 1000)
        return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()} UTC`
      }
      if (stringType === StringTypeEnum.ETH_TIME) {
        return `${new Date(Number(value) * 1000).toLocaleTimeString()} `
      }
      if (stringType === StringTypeEnum.ETH_DATE_TIME) {
        return `${new Date(Number(value) * 1000).toLocaleDateString()} ${new Date(
          Number(value) * 1000
        ).toLocaleTimeString()} `
      }
      if (stringType === StringTypeEnum.ETH_TIME_OF_DAY) {
        // seconds from 0:00 UTC e.g. 64800 is 18:00 UTC
        const secondsSinceMidnight = Number(value)
        const hour = Math.floor(secondsSinceMidnight / 3600)
        const rem = secondsSinceMidnight % 3600
        const min = Math.floor(rem / 60)
        const minDisplay = min < 10 ? `0${min}` : `${min}`
        return `${hour}:${minDisplay}  UTC`
      }
      if (stringType === StringTypeEnum.SINGLE_DAY_APR) {
        return `${(Number(value) / 1e8 / 1e8).toFixed(6) ?? ''} %`
      }
      if (stringType === StringTypeEnum.APR) {
        return `${(Number(value) / 1e2).toFixed(2) ?? ''} %`
      }

      return `${value}`
    },

    valueGetter: (_value: any, row?: any) => {
      if (!row) return ''
      const value = valueFromRow(row, property)

      return value
    },
  }
}
